/**
 * Cycle Count History Component
 * 
 * Displays cycle count history for a warehouse or vehicle target.
 * Read-only visibility of past cycle counts with insights summary.
 * Collapsible by default on narrow/portrait widths.
 * 
 * Note: Backend uses /api/audits routes (alias approach - DB tables remain unchanged)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { auditAPI } from '../../lib/api';
import api from '../../lib/api';
import { 
  ClipboardCheck, Clock, CheckCircle2, XCircle, 
  TrendingUp, TrendingDown, Minus, ChevronRight, ChevronDown,
  History, AlertCircle, BarChart3, ArrowUpDown, Calendar
} from 'lucide-react';
import { formatAuditTimestamp, getTenantTimezone } from '../../lib/timezone';

// Status badge variants
const STATUS_CONFIG = {
  in_progress: { label: 'In Progress', variant: 'secondary', icon: Clock },
  completed: { label: 'Completed', variant: 'default', icon: CheckCircle2 },
  abandoned: { label: 'Abandoned', variant: 'destructive', icon: XCircle }
};

export function CycleCountHistory({ targetType, targetId, returnTo }) {
  const navigate = useNavigate();
  const [cycleCounts, setCycleCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  
  // Collapsible state - default expanded on desktop (>768px), collapsed on narrow/portrait
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });

  useEffect(() => {
    if (targetType && targetId) {
      loadCycleCounts();
      loadInsights();
    }
  }, [targetType, targetId]);

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await auditAPI.getInsights({
        target_type: targetType,
        target_id: targetId
      });
      setInsights(response.data);
    } catch (err) {
      console.error('Error loading cycle count insights:', err);
      // Don't show error - insights are supplementary
    } finally {
      setInsightsLoading(false);
    }
  };
  
  // Get last cycle count info for collapsed header hint
  const getLastCycleCountHint = () => {
    if (!cycleCounts || cycleCounts.length === 0) return null;
    const lastCycleCount = cycleCounts[0];
    const date = lastCycleCount.status === 'completed' ? lastCycleCount.completed_at : lastCycleCount.started_at;
    if (!date) return null;
    
    // Use tenant timezone for accurate day comparison
    const timeZone = getTenantTimezone();
    const cycleCountDate = new Date(new Date(date).toLocaleString('en-US', { timeZone }));
    const today = new Date(new Date().toLocaleString('en-US', { timeZone }));
    const diffDays = Math.floor((today - cycleCountDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return '(last: Today)';
    if (diffDays === 1) return '(last: Yesterday)';
    if (diffDays < 7) return `(last: ${diffDays}d ago)`;
    return null;
  };

  const loadCycleCounts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Backend uses /audits route (alias approach)
      const response = await api.get('/audits', {
        params: { target_type: targetType, target_id: targetId }
      });
      setCycleCounts(response.data.audits || []);
    } catch (err) {
      setError('Failed to load cycle count history');
      console.error('Error loading cycle counts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCycleCountClick = (cycleCount) => {
    // Build return URL with encoded returnTo param
    const returnParam = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    navigate(`/cycle-count/${cycleCount.id}${returnParam}`);
  };

  // Use tenant timezone formatter
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatAuditTimestamp(dateString);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="w-5 h-5" />
            Cycle Count History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="w-5 h-5" />
            Cycle Count History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const lastCycleCountHint = getLastCycleCountHint();

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full text-left group" data-testid="cycle-count-history-toggle">
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="w-5 h-5" />
                Cycle Count History
                {!isOpen && lastCycleCountHint && (
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    {lastCycleCountHint}
                  </span>
                )}
              </CardTitle>
              <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {/* Insights Summary */}
            {insights && insights.completed_count > 0 && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg border" data-testid="cycle-count-insights-card">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Cycle Count Insights</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-muted-foreground text-xs">Completed</div>
                    <div className="font-semibold">{insights.completed_count}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Adjusted</div>
                    <div className="font-semibold">{insights.adjustments_applied_count}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Items w/ Variance</div>
                    <div className="font-semibold">{insights.total_items_with_variance}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Net Variance</div>
                    <div className={`font-semibold flex items-center gap-1 ${
                      insights.net_variance > 0 ? 'text-green-600' : 
                      insights.net_variance < 0 ? 'text-red-600' : ''
                    }`}>
                      {insights.net_variance > 0 ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : insights.net_variance < 0 ? (
                        <TrendingDown className="w-3 h-3" />
                      ) : null}
                      {insights.net_variance > 0 ? '+' : ''}{insights.net_variance}
                    </div>
                  </div>
                </div>
                {insights.last_completed_at && (
                  <div className="mt-2 pt-2 border-t text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Last completed: {formatAuditTimestamp(insights.last_completed_at)}
                  </div>
                )}
              </div>
            )}
            
            {cycleCounts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ClipboardCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No cycle counts yet</p>
                <p className="text-sm">Start a cycle count to track inventory accuracy</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="cycle-count-history-list">
                {cycleCounts.map(cycleCount => {
                  const statusConfig = STATUS_CONFIG[cycleCount.status] || STATUS_CONFIG.in_progress;
                  const StatusIcon = statusConfig.icon;
                  const displayDate = cycleCount.status === 'completed' 
                    ? cycleCount.completed_at 
                    : cycleCount.started_at;
                  
                  return (
                    <div
                      key={cycleCount.id}
                      className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleCycleCountClick(cycleCount)}
                      data-testid={`cycle-count-history-item-${cycleCount.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {/* Left side - main info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">
                              {cycleCount.audit_type === 'full' ? 'Full Cycle Count' : 'Category Cycle Count'}
                            </span>
                            <Badge variant={statusConfig.variant} className="text-xs">
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                          
                          {/* Date and started by */}
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{formatDate(displayDate)}</span>
                            <span className="text-muted-foreground/50">•</span>
                            <span>{cycleCount.started_by_name}</span>
                          </div>
                          
                          {/* Item counts */}
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-muted-foreground">
                              {cycleCount.counted_items}/{cycleCount.total_items} counted
                            </span>
                            {cycleCount.status === 'completed' && (
                              <div className="flex items-center gap-3">
                                {cycleCount.items_match > 0 && (
                                  <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircle2 className="w-3 h-3" />
                                    {cycleCount.items_match}
                                  </span>
                                )}
                                {cycleCount.items_short > 0 && (
                                  <span className="flex items-center gap-1 text-red-600">
                                    <TrendingDown className="w-3 h-3" />
                                    {cycleCount.items_short}
                                  </span>
                                )}
                                {cycleCount.items_over > 0 && (
                                  <span className="flex items-center gap-1 text-blue-600">
                                    <TrendingUp className="w-3 h-3" />
                                    {cycleCount.items_over}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Adjustments Applied indicator */}
                          {cycleCount.adjustments_applied_at && (
                            <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Adjustments applied {cycleCount.adjustments_applied_by_name ? `by ${cycleCount.adjustments_applied_by_name}` : ''}
                            </div>
                          )}
                        </div>
                        
                        {/* Right side - chevron */}
                        <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default CycleCountHistory;
